import React, { useMemo, useState, Dispatch, SetStateAction, useCallback, PropsWithChildren, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import isEqual from "lodash.isequal";
import useQueryString from "helpers/useQueryString";
import staticCache from "helpers/cache/Static";

interface Pager {
    total: number;
    total_page: number;
    last_page: number;
    per_page: number;
    current_page: number;
    order?: string;
    direction?: string;
    custom?: HashMap<number | undefined>;
}

type FetcherParams = HashMap<unknown>;
type ActiveFilters = HashMap<unknown>;
type ListData<T> = Array<T> & { pager: Pager } & { active_filters: ActiveFilters };

interface ListProviderInterface<T> {
    isLoading: boolean;
    hasError: boolean;
    error?: unknown;
    data: ListData<T>;
    refreshData: () => void;
    setLoading: Dispatch<SetStateAction<boolean>>;
    changeFetcherParams: (params?: FetcherParams) => void;
    selectedIds: Array<unknown>;
    setSelectedIds: (selectedIDs: Array<unknown>) => void;
    selectedId: unknown;
    setSelectedId: (selectedId: unknown) => void;
    wordingPagerTotalLocal?: string;
    setWordingPagerTotalLocal: (wording: string) => void;
}

const defaultListData: ListData<unknown> = Object.assign([], {
    pager: { total: 0, total_page: 1, last_page: 1, per_page: 50, current_page: 1 },
    active_filters: {},
});

export const listContextDefaultChangeFetcherParams = (_params?: FetcherParams) => {};

const ListContext = React.createContext<ListProviderInterface<unknown>>({
    isLoading: false,
    hasError: false,
    data: defaultListData,
    refreshData: () => {},
    setLoading: () => {},
    changeFetcherParams: listContextDefaultChangeFetcherParams,
    selectedIds: [],
    setSelectedIds: () => {},
    selectedId: undefined,
    setSelectedId: () => {},
    wordingPagerTotalLocal: "",
    setWordingPagerTotalLocal: () => {},
});

interface Props<T> {
    dataFetcher: (params?: FetcherParams) => Promise<ListData<T>>;
    defaultFetcherParams?: FetcherParams;
    cacheKey?: string;
    wordingPagerTotal?: string;
}

const getCache = <T,>(cacheKey: string | undefined, propertyName: string, defaultValue?: T): T | undefined => {
    if (cacheKey && staticCache.has(cacheKey)) {
        const cache = staticCache.get(cacheKey);
        return (cache[propertyName] as T | undefined) || defaultValue;
    }
    return defaultValue;
};

const setCache = (cacheKey: string | undefined, propertyName: string, value: unknown) => {
    if (cacheKey) {
        let merge: Record<string, unknown> = {};
        if (staticCache.has(cacheKey)) {
            merge = staticCache.get(cacheKey);
        }
        staticCache.set(cacheKey, Object.assign(merge, { [propertyName]: value }));
    }
};

function resolveListFetcherParams(
    defaultParams: FetcherParams | undefined,
    urlFilters: ActiveFilters,
    cacheKey?: string,
): FetcherParams {
    const merged = Object.assign({}, defaultParams ?? {}, urlFilters) as FetcherParams;
    return getCache(cacheKey, "fetcherParams", merged) ?? merged;
}

export function ListProvider<T>({
    dataFetcher,
    cacheKey,
    children,
    defaultFetcherParams,
    wordingPagerTotal,
}: PropsWithChildren<Props<T>>) {
    const queryString = useQueryString();

    const activeFilters = useMemo(() => {
        return (queryString.values ?? {}) as unknown as ActiveFilters;
    }, [queryString.values]);

    const initialListData = useMemo(() => {
        return Object.assign([], {
            pager: { total: 0, total_page: 1, last_page: 1, per_page: 50, current_page: 1 },
            active_filters: activeFilters,
        }) as ListData<T>;
    }, [activeFilters]);

    const resolvedFetcherParams = useMemo(
        () => resolveListFetcherParams(defaultFetcherParams, activeFilters, cacheKey),
        [defaultFetcherParams, activeFilters, cacheKey],
    );

    const queryScopeKey = cacheKey || dataFetcher.name || "list-provider";

    const [fetcherParams, setFetcherParams] = useState<FetcherParams | undefined>(resolvedFetcherParams);

    // Sync local state when URL filters (activeFilters) or default params change without remounting.
    // Deep-compare before setState so a freshly merged object with identical values does not
    // change the query key or trigger a second fetch (subcategory pages use usePreviewList instead).
    useEffect(() => {
        setFetcherParams((prev) => (isEqual(prev, resolvedFetcherParams) ? prev : resolvedFetcherParams));
    }, [resolvedFetcherParams]);
    const { isLoading, isError, error, data, refetch } = useQuery({
        queryKey: ["list-provider", queryScopeKey, fetcherParams],
        queryFn: () => dataFetcher(fetcherParams),
        // keep local skeleton-like defaults without marking the query as fresh
        placeholderData: initialListData,
        // previous usePromise behavior fetched on each mount/navigation
        refetchOnMount: "always",
    });

    const [isLoadingState, setLoading] = useState(false);
    const [selectedIds, setInternalSelectedIds] = useState<Array<unknown>>(getCache(cacheKey, "selectedIds", []) ?? []);
    const [selectedId, setSelectedId] = useState<unknown>(undefined);
    const [wordingPagerTotalLocal, setWordingPagerTotalLocal] = useState<string>(
        wordingPagerTotal || "crud.pager.total.items",
    );

    const setSelectedIds = useCallback(
        (selectedIds: Array<unknown>) => {
            setCache(cacheKey, "selectedIds", selectedIds);
            setInternalSelectedIds(selectedIds);
        },
        [setInternalSelectedIds, cacheKey],
    );

    const changeFetcherParams = useCallback(
        (params?: FetcherParams) => {
            const mergeParams = Object.assign({}, params);
            setSelectedIds([]);
            setCache(cacheKey, "fetcherParams", mergeParams);
            setFetcherParams(mergeParams as FetcherParams);
        },
        [setSelectedIds, cacheKey],
    );

    const refreshData = useCallback(() => {
        void refetch();
    }, [refetch]);

    return (
        <ListContext.Provider
            value={{
                isLoading: isLoadingState || isLoading,
                hasError: isError,
                error,
                data: (data ?? initialListData) as ListData<T>,
                refreshData,
                setLoading,
                changeFetcherParams,
                selectedIds,
                setSelectedIds,
                selectedId,
                setSelectedId,
                wordingPagerTotalLocal,
                setWordingPagerTotalLocal,
            }}
        >
            {children}
        </ListContext.Provider>
    );
}

const Consumer = ListContext.Consumer;

export { Consumer, ListProvider as Provider, ListContext as Context };
